import { MapWorld, type EntityId, type World } from "./world.js";
import { EventBus, type OpenEvents } from "./event-bus.js";
import { PLAYER_CONTROLLED_COMPONENT } from "./contracts.js";
import type {
  ActionHandler,
  GameModule,
  ModuleContext,
  SaveSlice,
  System,
  SystemOptions,
  SystemTickContext,
} from "./module.js";
import type { CompositionManifest } from "./manifest.js";
import type { ContentPack } from "./content.js";
import { SAVE_SCHEMA_VERSION, type Save } from "./save.js";
import { FIRST_SLICE_VERSION, migrateData } from "./migrate.js";
import { ServiceTable, type ServiceInfo, type ServiceKey } from "./services.js";
import { checkDeclarations, DeclarationError, type Declared, type DeclarationIssue, type HostProvides } from "./declarations.js";

// `createGame(manifest)` is what proves composition works: the kernel resolves the
// manifest's modules@versions against a registry, lets each self-register its systems /
// actions / save slice, builds the content pack's world, restores a save through the
// namespaced slices, and hands back a Game the renderer drives. The kernel owns no
// gameplay and names no specific module — it runs whatever the manifest hands it.
//
// Fail-soft systems (decision 0033; suggestion "Per-system error isolation and fail-soft subsystems"; open question
// 4 read as "isolation in play, fail-fast under the harness", 0068): every system runs inside a guard. Under the
// "isolate" policy a system that throws is logged once, switched off, and the other systems keep stepping. Under
// "fail-fast" (what a test harness sets) the same throw leaves `tick` as a `SystemFailure`, so the spec fails.

/** What a throwing system does to the game: switch itself off and let the rest step ("isolate", play), or end the
 *  tick with a {@link SystemFailure} ("fail-fast", tests and the harness). */
export type SystemErrorPolicy = "isolate" | "fail-fast";

/** A system's throw, as the error hook and `systemStatus()` report it. */
export interface SystemError {
  /** The system's name (its `SystemOptions.name`, or `module#n`). */
  system: string;
  /** The module that registered it. */
  module: string;
  /** The tick it threw on (the game's tick count, from 0). */
  tick: number;
  /** The thrown value's message. */
  message: string;
  /** The thrown value itself. */
  error: unknown;
}

/** The error `tick` throws under the "fail-fast" policy. `cause` is the system's own throw. */
export class SystemFailure extends Error {
  constructor(readonly report: SystemError) {
    super(`[kernel] system "${report.system}" (module "${report.module}") threw on tick ${report.tick}: ${report.message}`, { cause: report.error });
    this.name = "SystemFailure";
  }
}

/** One system's health, as `Game.systemStatus()` lists it. */
export interface SystemStatus {
  name: string;
  module: string;
  order: number;
  /** Ticks it ran to the end without throwing. */
  steps: number;
  /** Throws so far. */
  errors: number;
  /** Switched off by the isolate policy (or by `disableSystem`). */
  disabled: boolean;
  /** The first throw's message, if any. */
  firstError?: string;
  /** CPU ms spent in it since a timer was set ({@link Game.setTimer}); absent when never timed. */
  ms?: number;
  /** Ticks timed (the `ms` above covers these). */
  timed?: number;
}

/** Planted defects for proving the kernel's checks can fail (engine/CLAUDE.md). Off by default; read every tick. */
export interface KernelFaults {
  /** Remove the isolation guard: a throwing system escapes `tick` even under the "isolate" policy. */
  noIsolation: boolean;
  /** A pause stops nothing: every system runs while the game is paused (a cutscene's skip check must go red). */
  pauseIgnored: boolean;
}

export interface CreateGameOptions<M extends object = OpenEvents> {
  manifest: CompositionManifest;
  /** id -> module implementation, resolved by the composition (dev-owned, static today).
   *  The kernel never imports a module; the composition wires this registry. */
  moduleRegistry: Record<string, GameModule<M>>;
  content: ContentPack<M>;
  /** A prior save to resume from. Pass null/undefined for a fresh game. */
  save?: Save | null;
  /** Host-injected services keyed by a well-known string (e.g. "judgment" → a JudgmentProvider): an instance, or a
   *  `lazyProvider(() => …)` factory run on first use. Modules read them with `ctx.getService(key)` (a provider or
   *  the key's stub) or the older `ctx.getProvider(key)` (a provider or undefined). See services.ts. */
  providers?: Record<string, unknown>;
  /** What a throwing system does. Default "isolate". A harness sets "fail-fast". */
  errorPolicy?: SystemErrorPolicy;
  /** Where an isolated system's error (and a service fallback) is logged. Default `console.error`, which the
   *  harness's error capture records. Called once per system: the first throw. */
  onSystemError?: (error: SystemError) => void;
  /** Throws before an isolated system is switched off. Default 1 (the first throw). */
  disableAfter?: number;
  /** What the host itself provides for the declaration check: events emitted outside modules, services and HUD panels
   *  it wires. The keys of `providers` count as provided services already. */
  hostProvides?: HostProvides;
  /** What a declaration problem does: "fail-fast" throws a {@link DeclarationError} from createGame, "warn" logs each
   *  problem once (to `onSystemError`'s console) and boots. Default: "fail-fast" under the fail-fast error policy,
   *  else "warn". */
  declarationPolicy?: "fail-fast" | "warn";
}

export interface Game<M extends object = OpenEvents> {
  readonly world: World;
  readonly events: EventBus<M>;
  readonly manifest: CompositionManifest;
  /** The error policy this game runs under. */
  readonly errorPolicy: SystemErrorPolicy;
  /** Planted-defect switches; see {@link KernelFaults}. */
  readonly faults: KernelFaults;
  /** Advance all registered systems by `dt` seconds (only the `whilePaused` ones while paused). Under "fail-fast" a
   *  throwing system throws a {@link SystemFailure}. */
  tick(dt: number): void;
  /** Pause for `reason`, or lift that reason's pause (as {@link ModuleContext.pause}). */
  pause(reason: string, on: boolean): void;
  /** The reasons the game is paused for now (empty: running). */
  paused(): readonly string[];
  /** Invoke a module-registered action (e.g. "moveIntent"). No-op + warn if unknown. */
  dispatch(action: string, payload?: unknown): void;
  /** The avatar the camera follows / input drives, or null if content tagged none. */
  getPlayerEntity(): EntityId | null;
  /** Snapshot progress into a composition-bound, namespaced-slice save. */
  serialize(): Save;
  /** Restore a save into this running game through the same namespaced slices as a save passed at creation
   *  (each slice overwrites its module's state; a slice for an absent module is dropped). Entities are not
   *  re-created: a save restores into a world built by the same composition and content. */
  restore(save: Save): void;
  /** The registered systems' names, in run order. */
  systems(): string[];
  /** Every system's steps, errors and on/off state, in run order. */
  systemStatus(): SystemStatus[];
  /** The names of systems switched off. */
  disabledSystems(): string[];
  /** Switch a system back on (a dev switch) or off. Returns false for an unknown name. */
  setSystemEnabled(name: string, on: boolean): boolean;
  /** Time every system with `now` (ms; a dev profiler passes `performance.now`), or stop with null. The kernel never
   *  reads a clock itself, and timing changes no state. Times add up in `systemStatus()`'s `ms` and `timed`. */
  setTimer(now: (() => number) | null): void;
  /** A service by its typed key: the host's provider or the key's stub. */
  getService<T>(key: ServiceKey<T>): T;
  /** Every service resolved so far and how (provided, stub, stub-fallback). */
  services(): ServiceInfo[];
  /** The declaration check's result at boot: its problems (empty when the declarations add up) and how many modules
   *  declared anything. */
  declarations(): { issues: DeclarationIssue[]; declared: number };
}

interface SystemEntry<M extends object> {
  run: System<M>;
  order: number;
  seq: number;
  name: string;
  module: string;
  whilePaused: boolean;
  steps: number;
  errors: number;
  disabled: boolean;
  firstError?: string;
  ms: number;
  timed: number;
}

function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function createGame<M extends object = OpenEvents>(opts: CreateGameOptions<M>): Game<M> {
  const { manifest, moduleRegistry, content } = opts;
  const world = new MapWorld();
  const events = new EventBus<M>();
  const errorPolicy: SystemErrorPolicy = opts.errorPolicy ?? "isolate";
  const disableAfter = Math.max(1, opts.disableAfter ?? 1);
  const report =
    opts.onSystemError ??
    ((e: SystemError): void =>
      console.error(`[kernel] system "${e.system}" (module "${e.module}") threw on tick ${e.tick} and is switched off: ${e.error instanceof Error ? (e.error.stack ?? e.message) : e.message}`));
  const faults: KernelFaults = { noIsolation: false, pauseIgnored: false };
  // the reasons the game is paused for (a set: two cutscenes or a cutscene and a menu each lift only their own)
  const pauses = new Set<string>();
  const pause = (reason: string, on: boolean): void => void (on ? pauses.add(reason) : pauses.delete(reason));
  const services = new ServiceTable(opts.providers ?? {}, (m) => console.error(m));

  const loadedIds = new Set(manifest.modules.map((m) => m.id));

  // --- Check the modules' declarations before any setup runs ----------------
  const listed = manifest.modules.map((ref) => moduleRegistry[ref.id]).filter((m): m is GameModule<M> => m !== undefined);
  const declared = listed.filter((m) => m.provides !== undefined || m.needs !== undefined);
  const host: HostProvides = { ...opts.hostProvides, services: [...(opts.hostProvides?.services ?? []), ...Object.keys(opts.providers ?? {})] };
  const declarationIssues = declared.length ? checkDeclarations(declared as unknown as Declared[], host) : [];
  if (declarationIssues.length) {
    const policy = opts.declarationPolicy ?? (errorPolicy === "fail-fast" ? "fail-fast" : "warn");
    if (policy === "fail-fast") throw new DeclarationError(declarationIssues);
    for (const i of declarationIssues) console.error(`[kernel] declarations: ${i.message}`);
  }
  const systems: SystemEntry<M>[] = [];
  const actions = new Map<string, ActionHandler>();
  // Insertion order preserved; serialize() walks this so save key order is stable.
  const slices = new Map<string, SaveSlice>();
  let tickCount = 0;

  // --- Resolve + self-register each module the manifest lists ---------------
  for (const ref of manifest.modules) {
    const mod = moduleRegistry[ref.id];
    if (!mod) {
      throw new Error(
        `createGame: manifest lists module "${ref.id}" but no implementation is in the registry`,
      );
    }
    if (mod.version !== ref.version) {
      // Static dev-owned composition today; a mismatch is a wiring smell, not fatal.
      console.warn(
        `[kernel] module "${ref.id}" manifest wants ${ref.version}, registry has ${mod.version}`,
      );
    }
    for (const dep of mod.dependencies ?? []) {
      if (!loadedIds.has(dep)) {
        // Graceful degradation: the module loads; reactions needing the absent peer
        // simply won't fire.
        console.warn(
          `[kernel] module "${ref.id}" expects "${dep}", which the manifest doesn't load`,
        );
      }
    }

    const ctx: ModuleContext<M> = {
      world,
      events,
      registerSystem: (s: System<M>, o: SystemOptions = {}) => {
        systems.push({
          run: s,
          order: o.order ?? 0,
          seq: systems.length,
          name: o.name ?? `${ref.id}#${systems.length}`,
          module: ref.id,
          whilePaused: o.whilePaused === true,
          steps: 0,
          errors: 0,
          disabled: false,
          ms: 0,
          timed: 0,
        });
        // stable: equal orders keep registration order (every system at the default 0 runs exactly as before)
        systems.sort((a, b) => a.order - b.order || a.seq - b.seq);
      },
      registerAction: (name, handler) => {
        if (actions.has(name)) {
          console.warn(`[kernel] action "${name}" registered twice; last wins`);
        }
        actions.set(name, handler);
      },
      registerSaveSlice: (slice) => slices.set(ref.id, slice),
      pause,
      paused: () => [...pauses],
      hasModule: (id) => loadedIds.has(id),
      getProvider: <T>(key: string): T | undefined =>
        (opts.providers?.[key] as T | undefined) ?? undefined,
      getService: (key) => services.get(key),
    };
    mod.setup(ctx);
  }

  // --- Build the content pack's world --------------------------------------
  content.build({ world, events });

  // --- Restore a save, if any ----------------------------------------------
  // Order matters: content.build() spawned everything at defaults; slices then overwrite
  // with saved state (e.g. spatial-movement sets the player's Transform to the saved spot).
  const applySave = (save: Save): void => {
    // Migrate every slice first, so a slice that can't be migrated throws before any state changes.
    const migrated: [SaveSlice, unknown][] = [];
    for (const [id, slice] of slices) {
      const data = save.modules[id];
      // Absent slice (module wasn't present when the save was made, or vice-versa) is
      // dropped gracefully — the module keeps its current state (content.build() defaults at boot).
      if (data === undefined) continue;
      const from = save.sliceVersions?.[id] ?? FIRST_SLICE_VERSION;
      migrated.push([slice, migrateData(id, from, slice.version ?? FIRST_SLICE_VERSION, data, slice.migrations)]);
    }
    for (const [slice, data] of migrated) slice.deserialize(data);
    if (save.contentState != null) content.deserializeState?.(save.contentState);
  };
  if (opts.save) applySave(opts.save);

  let timer: (() => number) | null = null;
  const runSystem = (s: SystemEntry<M>, ctx: SystemTickContext<M>): void => {
    if (timer) {
      const t0 = timer();
      try {
        guarded(s, ctx);
      } finally {
        s.ms += timer() - t0;
        s.timed++;
      }
      return;
    }
    guarded(s, ctx);
  };
  const guarded = (s: SystemEntry<M>, ctx: SystemTickContext<M>): void => {
    if (faults.noIsolation) {
      s.run(ctx);
      s.steps++;
      return;
    }
    try {
      s.run(ctx);
      s.steps++;
    } catch (error) {
      s.errors++;
      const e: SystemError = { system: s.name, module: s.module, tick: tickCount, message: messageOf(error), error };
      if (s.errors === 1) s.firstError = e.message;
      if (errorPolicy === "fail-fast") throw new SystemFailure(e);
      if (s.errors === 1) report(e);
      if (s.errors >= disableAfter) s.disabled = true;
    }
  };

  return {
    world,
    events,
    manifest,
    errorPolicy,
    faults,
    tick(dt: number): void {
      const held = pauses.size > 0 && !faults.pauseIgnored;
      const ctx: SystemTickContext<M> = { world, events, dt, paused: pauses.size > 0 };
      for (const system of systems) if (!system.disabled && (!held || system.whilePaused)) runSystem(system, ctx);
      tickCount++;
    },
    pause,
    paused: () => [...pauses],
    dispatch(action: string, payload?: unknown): void {
      const handler = actions.get(action);
      if (!handler) {
        console.warn(`[kernel] dispatch("${action}"): no module registered this action`);
        return;
      }
      handler(payload);
    },
    getPlayerEntity(): EntityId | null {
      return world.query(PLAYER_CONTROLLED_COMPONENT)[0] ?? null;
    },
    serialize(): Save {
      const moduleState: Record<string, unknown> = {};
      const sliceVersions: Record<string, number> = {};
      for (const [id, slice] of slices) {
        moduleState[id] = slice.serialize();
        // version 1 is the default: only later versions are written, so a save of unversioned slices keeps its shape
        if ((slice.version ?? FIRST_SLICE_VERSION) !== FIRST_SLICE_VERSION) sliceVersions[id] = slice.version!;
      }
      return {
        schemaVersion: SAVE_SCHEMA_VERSION,
        composition: { modules: manifest.modules.map((m) => ({ ...m })) },
        kernel: {},
        modules: moduleState,
        contentState: content.serializeState?.() ?? null,
        ...(Object.keys(sliceVersions).length ? { sliceVersions } : {}),
      };
    },
    restore(save: Save): void {
      applySave(save);
    },
    systems(): string[] {
      return systems.map((s) => s.name);
    },
    systemStatus(): SystemStatus[] {
      return systems.map((s) => ({
        name: s.name,
        module: s.module,
        order: s.order,
        steps: s.steps,
        errors: s.errors,
        disabled: s.disabled,
        ...(s.firstError !== undefined ? { firstError: s.firstError } : {}),
        ...(s.timed > 0 ? { ms: s.ms, timed: s.timed } : {}),
      }));
    },
    setTimer(now: (() => number) | null): void {
      timer = now;
    },
    disabledSystems(): string[] {
      return systems.filter((s) => s.disabled).map((s) => s.name);
    },
    setSystemEnabled(name: string, on: boolean): boolean {
      const s = systems.find((x) => x.name === name);
      if (!s) return false;
      s.disabled = !on;
      if (on) s.errors = 0;
      return true;
    },
    getService: (key) => services.get(key),
    services: () => services.list(),
    declarations: () => ({ issues: declarationIssues.map((i) => ({ ...i })), declared: declared.length }),
  };
}
